import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { useAuth } from "../hooks/useAuth";
import { Link } from "react-router-dom";
import api from "../api/API";
import publicApi, { PUBLIC_API_KEY_STORAGE } from "../api/publicApi";
import MembersManager from "../components/MembersManager";
import { useWorkspace } from "../context/WorkspaceContext";
import "./PerfilPage.css";

const AVAILABLE_SCOPES = ["read:tasks", "read:projects", "write:tasks"];
const DEFAULT_KEY_NAME = "Integracao pessoal";

const getInitials = (user) => {
  const rawName = user?.nome || user?.name || user?.email || "";
  const parts = rawName.trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) return "US";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const maskApiKey = (key = "") => {
  if (!key) return "Nenhuma chave aplicada";
  if (key.length <= 8) return key;

  return `${key.slice(0, 4)}...${key.slice(-4)}`;
};

const formatDateTime = (value) => {
  if (!value) return "Nunca usado";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Data invalida";

  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const parseRecords = (response) => {
  if (!response?.data?.status) {
    throw new Error(response?.data?.message || "Nao foi possivel carregar as API keys.");
  }

  return response.data.records || [];
};

const parseMutation = (response, fallbackMessage) => {
  if (!response?.data?.status) {
    throw new Error(response?.data?.message || fallbackMessage);
  }

  return response.data;
};

export default function PerfilPage() {
  const { user } = useAuth();
  const { selectedWorkspaceId, selectedWorkspace, loading: loadingWorkspace, error: workspaceError } = useWorkspace();
  const [apiKeys, setApiKeys] = useState([]);
  const [loadingApiKeys, setLoadingApiKeys] = useState(true);
  const [creatingApiKey, setCreatingApiKey] = useState(false);
  const [processingApiKeyId, setProcessingApiKeyId] = useState(null);
  const [creatingResult, setCreatingResult] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [newKeyName, setNewKeyName] = useState(DEFAULT_KEY_NAME);
  const [selectedScopes, setSelectedScopes] = useState(AVAILABLE_SCOPES);
  const [generatedApiKey, setGeneratedApiKey] = useState("");
  const [appliedApiKey, setAppliedApiKey] = useState("");
  const [draftApiKey, setDraftApiKey] = useState("");
  const [showGeneratedKey, setShowGeneratedKey] = useState(false);
  const [showDraftKey, setShowDraftKey] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);

  useEffect(() => {
    const currentKey = localStorage.getItem(PUBLIC_API_KEY_STORAGE) || "";
    setAppliedApiKey(currentKey);
    setDraftApiKey(currentKey);
  }, []);

  useEffect(() => {
    loadApiKeys();
  
  }, []);

  const userDisplayName = useMemo(
    () => user?.nome || user?.descricao || "Usuario LevelUp",
    [user]
  );

  const userEmail = useMemo(
    () => user?.email || "E-mail nao informado",
    [user]
  );

  async function loadApiKeys() {
    setLoadingApiKeys(true);

    try {
      const response = await api.get("/user/api-keys");
      const records = parseRecords(response);
      setApiKeys(records);
    } catch (error) {
      setCreatingResult({
        variant: "danger",
        title: "Nao foi possivel carregar as API keys.",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Falha ao consultar as chaves do usuario.",
      });
    } finally {
      setLoadingApiKeys(false);
    }
  }

  const handleScopeToggle = (scope) => {
    setSelectedScopes((currentScopes) =>
      currentScopes.includes(scope)
        ? currentScopes.filter((currentScope) => currentScope !== scope)
        : [...currentScopes, scope]
    );
  };

  const handleCreateApiKey = async () => {
    const normalizedName = newKeyName.trim();

    if (!normalizedName) {
      setCreatingResult({
        variant: "warning",
        title: "Informe um nome para a chave.",
        description: "Esse nome ajuda a identificar a integracao na listagem.",
      });
      return;
    }

    if (selectedScopes.length === 0) {
      setCreatingResult({
        variant: "warning",
        title: "Selecione pelo menos um escopo.",
        description: "A API key precisa de ao menos uma permissao para ser criada.",
      });
      return;
    }

    setCreatingApiKey(true);
    setCreatingResult(null);

    try {
      const response = await api.post("/user/api-keys", {
        nome: normalizedName,
        scopes: selectedScopes,
      });

      const payload = parseMutation(response, "Nao foi possivel criar a API key.");
      const rawApiKey = payload.apiKey || "";

      setGeneratedApiKey(rawApiKey);
      setDraftApiKey(rawApiKey);
      setAppliedApiKey(rawApiKey);
      setShowGeneratedKey(true);
      setShowDraftKey(true);
      localStorage.setItem(PUBLIC_API_KEY_STORAGE, rawApiKey);

      setCreatingResult({
        variant: "success",
        title: "API key criada com sucesso.",
        description:
          payload.message ||
          "A chave bruta foi retornada uma unica vez e ja ficou aplicada para teste local.",
      });

      setNewKeyName(DEFAULT_KEY_NAME);
      setSelectedScopes(AVAILABLE_SCOPES);
      await loadApiKeys();
    } catch (error) {
      setCreatingResult({
        variant: "danger",
        title: "Falha ao criar a API key.",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Nao foi possivel gerar a chave agora.",
      });
    } finally {
      setCreatingApiKey(false);
    }
  };

  const handleApiKeyStatusChange = async (recordId, action) => {
    setProcessingApiKeyId(recordId);
    setCreatingResult(null);

    try {
      const response = await api.patch(`/user/api-keys/${recordId}/${action}`, {});
      parseMutation(response, "Nao foi possivel atualizar a API key.");

      setCreatingResult({
        variant: "success",
        title: action === "activate" ? "Chave ativada." : "Chave revogada.",
        description: "A listagem foi atualizada com o novo status.",
      });

      await loadApiKeys();
    } catch (error) {
      setCreatingResult({
        variant: "danger",
        title: "Nao foi possivel atualizar a chave.",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Falha ao alterar o status da API key.",
      });
    } finally {
      setProcessingApiKeyId(null);
    }
  };

  const handleApplyKeyForTests = () => {
    const normalizedKey = draftApiKey.trim();

    if (!normalizedKey) {
      setTestResult({
        variant: "warning",
        title: "Nenhuma chave disponivel para teste.",
        description: "Crie uma API key nova ou cole uma chave valida antes de testar.",
      });
      return;
    }

    localStorage.setItem(PUBLIC_API_KEY_STORAGE, normalizedKey);
    setAppliedApiKey(normalizedKey);
    setDraftApiKey(normalizedKey);
    setTestResult({
      variant: "success",
      title: "Chave aplicada aos testes.",
      description: "As proximas chamadas a API publica usarao essa chave salva localmente.",
    });
  };

  const handleClearAppliedKey = () => {
    localStorage.removeItem(PUBLIC_API_KEY_STORAGE);
    setAppliedApiKey("");
    setDraftApiKey("");
    setTestResult({
      variant: "warning",
      title: "Chave removida.",
      description: "Nenhuma credencial publica esta configurada neste navegador.",
    });
  };

  const handleCopyKey = async (value, successTitle) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      setTestResult({
        variant: "success",
        title: successTitle,
        description: "A chave foi copiada para a area de transferencia.",
      });
    } catch (error) {
      setTestResult({
        variant: "warning",
        title: "Nao foi possivel copiar automaticamente.",
        description: "Use o botao de visualizar e copie a chave manualmente.",
      });
    }
  };

  const handleTestConnection = async () => {
    if (!appliedApiKey) {
      setTestResult({
        variant: "warning",
        title: "Aplique uma chave antes de testar.",
        description: "A API publica exige o header x-api-key em todas as requisicoes.",
      });
      return;
    }

    setTestingConnection(true);
    setTestResult(null);

    try {
      const response = await publicApi.get("/context");
      const resumo = response?.data?.data?.resumo;

      setTestResult({
        variant: "success",
        title: "Conexao validada.",
        description: resumo
          ? `Resumo retornado: ${resumo.concluidas ?? 0} concluidas de ${resumo.total ?? 0} tarefas.`
          : "A API respondeu corretamente ao endpoint /context.",
      });
    } catch (error) {
      const status = error?.response?.status;
      const serverMessage =
        error?.response?.data?.data?.message ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Nao foi possivel validar a chave agora.";

      setTestResult({
        variant: "danger",
        title: status === 401 ? "Chave invalida ou revogada." : "Falha ao validar a integracao.",
        description: serverMessage,
      });
    } finally {
      setTestingConnection(false);
    }
  };

  return (
    <Container fluid className="perfil-page">
      <div className="perfil-hero">
        <div className="perfil-avatar">{getInitials(user)}</div>

        <div>
          <p className="perfil-kicker">Perfil e integracoes</p>
          <h1>{userDisplayName}</h1>
          <p className="perfil-subtitle">
            Gerencie API keys do usuario autenticado via JWT e use a chave bruta
            retornada na criacao para testar a API publica com <code>x-api-key</code>.
          </p>
        </div>
      </div>

      <Row className="g-4">
        <Col lg={4}>
          <Card className="perfil-card h-100">
            <Card.Body>
              <h2>Minha conta</h2>
              <p className="text-muted mb-4">
                Informacoes carregadas da sessao autenticada atual.
              </p>

              <div className="perfil-info-list">
                <div className="perfil-info-item">
                  <span>Nome</span>
                  <strong>{userDisplayName}</strong>
                </div>

                <div className="perfil-info-item">
                  <span>E-mail</span>
                  <strong>{userEmail}</strong>
                </div>

                <div className="perfil-info-item">
                  <span>API key aplicada no navegador</span>
                  <strong>{appliedApiKey ? "Configurada" : "Pendente"}</strong>
                </div>

                <div className="perfil-info-item">
                  <span>Chave aplicada</span>
                  <strong>{maskApiKey(appliedApiKey)}</strong>
                </div>

                <div className="perfil-info-item">
                  <span>Workspace ativo</span>
                  <strong>{selectedWorkspace?.nome || "Nenhum selecionado"}</strong>
                </div>
              </div>

              <Button
                as={Link}
                to="/perfil/workspaces"
                variant="outline-primary"
                className="mt-4 w-100"
              >
                Gerenciar Workspaces
              </Button>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={8}>
          <Card className="perfil-card mb-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
                <div>
                  <h2>Gerar nova API key</h2>
                  <p className="text-muted mb-0">
                    O cadastro usa <code>POST /api/user/api-keys</code> com JWT e escopos selecionados.
                  </p>
                </div>

                <Badge bg="success" className="perfil-badge">
                  JWT protegido
                </Badge>
              </div>

              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Nome da integracao</Form.Label>
                  <Form.Control
                    type="text"
                    value={newKeyName}
                    onChange={(event) => setNewKeyName(event.target.value)}
                    placeholder="Ex.: n8n pessoal"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Escopos</Form.Label>
                  <div className="perfil-scopes-grid">
                    {AVAILABLE_SCOPES.map((scope) => (
                      <label key={scope} className="perfil-scope-option">
                        <Form.Check
                          type="checkbox"
                          checked={selectedScopes.includes(scope)}
                          onChange={() => handleScopeToggle(scope)}
                          label={scope}
                        />
                      </label>
                    ))}
                  </div>
                </Form.Group>

                <div className="perfil-actions">
                  <Button
                    variant="primary"
                    type="button"
                    onClick={handleCreateApiKey}
                    disabled={creatingApiKey}
                  >
                    {creatingApiKey ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Gerando...
                      </>
                    ) : (
                      "Gerar API key"
                    )}
                  </Button>
                </div>
              </Form>

              {creatingResult && (
                <Alert variant={creatingResult.variant} className="mt-4 mb-0">
                  <Alert.Heading>{creatingResult.title}</Alert.Heading>
                  <p className="mb-0">{creatingResult.description}</p>
                </Alert>
              )}
            </Card.Body>
          </Card>

          {generatedApiKey && (
            <Card className="perfil-card mb-4">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
                  <div>
                    <h2>Chave criada agora</h2>
                    <p className="text-muted mb-0">
                      O backend devolve a chave bruta uma unica vez. Guarde-a agora se precisar usar fora deste navegador.
                    </p>
                  </div>

                  <Badge bg="warning" text="dark" className="perfil-badge">
                    Retorno unico
                  </Badge>
                </div>

                <div className="perfil-key-row">
                  <Form.Control
                    type={showGeneratedKey ? "text" : "password"}
                    readOnly
                    value={generatedApiKey}
                  />

                  <Button
                    variant="outline-secondary"
                    type="button"
                    onClick={() => setShowGeneratedKey((current) => !current)}
                  >
                    {showGeneratedKey ? "Ocultar" : "Visualizar"}
                  </Button>

                  <Button
                    variant="outline-primary"
                    type="button"
                    onClick={() => handleCopyKey(generatedApiKey, "Chave copiada.")}
                  >
                    Copiar
                  </Button>
                </div>
              </Card.Body>
            </Card>
          )}

          <Card className="perfil-card mb-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
                <div>
                  <h2>Testar API publica</h2>
                  <p className="text-muted mb-0">
                    A chave abaixo e a credencial aplicada localmente para chamadas em <code>/api/v1</code>.
                  </p>
                </div>

                <Badge bg={appliedApiKey ? "success" : "secondary"} className="perfil-badge">
                  {appliedApiKey ? "Pronta para teste" : "Sem chave aplicada"}
                </Badge>
              </div>

              <Form.Group className="mb-3">
                <Form.Label>API key aplicada no navegador</Form.Label>
                <div className="perfil-key-row">
                  <Form.Control
                    type={showDraftKey ? "text" : "password"}
                    placeholder="lvlp_xxxxxxxxxxxxxxxxxxxxxxxx"
                    value={draftApiKey}
                    onChange={(event) => setDraftApiKey(event.target.value)}
                  />

                  <Button
                    variant="outline-secondary"
                    type="button"
                    onClick={() => setShowDraftKey((current) => !current)}
                  >
                    {showDraftKey ? "Ocultar" : "Visualizar"}
                  </Button>

                  <Button
                    variant="outline-primary"
                    type="button"
                    onClick={handleApplyKeyForTests}
                  >
                    Aplicar
                  </Button>
                </div>
                <Form.Text className="text-muted">
                  Essa chave fica salva em <code>localStorage</code> para a area de testes da API publica.
                </Form.Text>
              </Form.Group>

              <div className="perfil-actions">
                <Button variant="outline-primary" onClick={handleTestConnection} disabled={testingConnection}>
                  {testingConnection ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Testando...
                    </>
                  ) : (
                    "Testar conexao publica"
                  )}
                </Button>

                <Button variant="outline-secondary" onClick={() => handleCopyKey(appliedApiKey, "Chave aplicada copiada.")} disabled={!appliedApiKey}>
                  Copiar chave aplicada
                </Button>

                <Button variant="outline-secondary" onClick={handleClearAppliedKey}>
                  Limpar chave aplicada
                </Button>
              </div>

              {testResult && (
                <Alert variant={testResult.variant} className="mt-4 mb-0">
                  <Alert.Heading>{testResult.title}</Alert.Heading>
                  <p className="mb-0">{testResult.description}</p>
                </Alert>
              )}
            </Card.Body>
          </Card>

          <Card className="perfil-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
                <div>
                  <h2>Minhas API keys</h2>
                  <p className="text-muted mb-0">
                    A listagem vem de <code>GET /api/user/api-keys</code> e mostra apenas metadados.
                  </p>
                </div>

                <Badge bg="light" text="dark" className="perfil-badge">
                  {apiKeys.length} chave(s)
                </Badge>
              </div>

              {loadingApiKeys ? (
                <div className="perfil-empty-state">
                  <Spinner size="sm" className="me-2" />
                  Carregando chaves...
                </div>
              ) : apiKeys.length === 0 ? (
                <div className="perfil-empty-state">Nenhuma API key cadastrada ainda.</div>
              ) : (
                <div className="perfil-keys-list">
                  {apiKeys.map((record) => {
                    const isActive = Boolean(record.ativo);
                    const createdAt = record.criado_em || record.created_at;
                    const lastUsedAt = record.ultimo_uso || record.last_used_at;
                    const scopes = Array.isArray(record.scopes) ? record.scopes : [];

                    return (
                      <div key={record.id} className="perfil-key-card">
                        <div className="perfil-key-card-top">
                          <div>
                            <h3>{record.nome || "Integracao sem nome"}</h3>
                            <p className="mb-0 text-muted">
                              Criada em {formatDateTime(createdAt)}
                            </p>
                          </div>

                          <Badge bg={isActive ? "success" : "secondary"}>
                            {isActive ? "Ativa" : "Revogada"}
                          </Badge>
                        </div>

                        <div className="perfil-meta-grid">
                          <div>
                            <span>Ultimo uso</span>
                            <strong>{formatDateTime(lastUsedAt)}</strong>
                          </div>

                          <div>
                            <span>Escopos</span>
                            <strong>{scopes.length > 0 ? scopes.join(", ") : "Sem escopos"}</strong>
                          </div>
                        </div>

                        {scopes.length > 0 && (
                          <div className="perfil-inline-scopes">
                            {scopes.map((scope) => (
                              <Badge key={`${record.id}-${scope}`} bg="light" text="dark" className="perfil-scope">
                                {scope}
                              </Badge>
                            ))}
                          </div>
                        )}

                        <div className="perfil-actions">
                          {isActive ? (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              disabled={processingApiKeyId === record.id}
                              onClick={() => handleApiKeyStatusChange(record.id, "revoke")}
                            >
                              {processingApiKeyId === record.id ? "Revogando..." : "Revogar"}
                            </Button>
                          ) : (
                            <Button
                              variant="outline-success"
                              size="sm"
                              disabled={processingApiKeyId === record.id}
                              onClick={() => handleApiKeyStatusChange(record.id, "activate")}
                            >
                              {processingApiKeyId === record.id ? "Ativando..." : "Ativar"}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card.Body>
          </Card>

          <Card className="perfil-card mt-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
                <div>
                  <h2>Membros do workspace</h2>
                  <p className="text-muted mb-0">
                    Este passa a ser o ponto principal para convidar pessoas, ajustar papeis e administrar o time do workspace.
                  </p>
                </div>

                <Badge bg="primary" className="perfil-badge">
                  {selectedWorkspace?.nome || "Workspace"}
                </Badge>
              </div>

              {workspaceError && <Alert variant="warning">{workspaceError}</Alert>}

              {!selectedWorkspaceId && !loadingWorkspace ? (
                <div className="perfil-empty-state">
                  Selecione um workspace no topo da aplicacao para gerir membros e convites.
                </div>
              ) : (
                <div className="perfil-members-shell">
                  <MembersManager workspaceId={selectedWorkspaceId} />
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
